import React, { useMemo, useState } from "react";
import {
  Box,
  styled,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Checkbox,
  CircularProgress,
  Alert,
  FormControlLabel,
  Table as MUITable,
  TableBody as MUITableBody,
  TableCell as MUITableCell,
  TableHead as MUITableHead,
  TableRow as MUITableRow,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  sendEmailAddUser,
  SendEmailAddUserPayload,
  getUserTypes,
  UserType,
  checkUserExistsByEmail,
  ExistingUserData,
} from "@services/UserManagement";
import AffableConnectedIcon from "@components/Spaces/AffableConnectedIcon";
import { formatFacilityDropdownLabel } from "@utils/facilityUtils";
import { getFeatureDisplayName, filterFeaturesForPermissionEditor } from "@utils/permissions";
import { formatUserTypeDisplayName } from "@utils/userUtils";
import {
  fetchFeatures,
  fetchUserPermissionsByUserId,
  saveUserPermissions,
  SavePermissionInput,
} from "@services/UserPermission";

type CheckedPermissionFlags = {
  view: boolean;
  add: boolean;
  update: boolean;
  delete: boolean;
};

/**
 * API may send booleans as strings ("false", "0") — `Boolean("false")` is wrongly true in JS.
 */
function coercePermissionFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined) {
    return false;
  }
  if (typeof value === "number") {
    return value !== 0 && !Number.isNaN(value);
  }
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no" || s === "") return false;
    const n = Number(s);
    if (s !== "" && !Number.isNaN(n)) return n !== 0;
  }
  return false;
}

/** Coerce row values so string `"false"` / `"0"` never drive `checked` as truthy. */
function normalizePermissionRow(raw: unknown): CheckedPermissionFlags {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      view: false,
      add: false,
      update: false,
      delete: false,
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    view: coercePermissionFlag(o.view),
    add: coercePermissionFlag(o.add),
    update: coercePermissionFlag(o.update),
    delete: coercePermissionFlag(o.delete),
  };
}

function parseDefaultCheckedPermissionEntry(
  raw: unknown,
): CheckedPermissionFlags | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return normalizePermissionRow(raw);
}

function getPermissionRowFromMatrix(
  matrix: Record<string | number, unknown>,
  featureId: string | number,
): CheckedPermissionFlags {
  const keys = new Set<string | number>();
  keys.add(featureId);
  keys.add(String(featureId));
  const num = Number(featureId);
  if (!Number.isNaN(num)) keys.add(num);

  for (const k of keys) {
    const raw = matrix[k];
    if (raw !== undefined && raw !== null) {
      return normalizePermissionRow(raw);
    }
  }
  return normalizePermissionRow(undefined);
}

/**
 * Read {@link default_checked_permissions}[userTypeId] on a single feature. Keys match
 * user type ids from GET `/api/v1/user-types` (e.g. "1", "8").
 */
function pickCheckedPermissionsForUserType(
  map: Record<string, unknown>,
  userTypeId: number,
): CheckedPermissionFlags | null {
  if (!map || typeof map !== "object" || Array.isArray(map)) return null;

  const tryKey = (key: string): CheckedPermissionFlags | null => {
    if (!Object.prototype.hasOwnProperty.call(map, key)) return null;
    return parseDefaultCheckedPermissionEntry(map[key]);
  };

  const uid = Number(userTypeId);
  if (Number.isNaN(uid)) return null;

  // Use !== null so all-false defaults (Standard User / Add Facility) still win.
  const direct = tryKey(String(userTypeId).trim());
  if (direct !== null) return direct;

  for (const k of Object.keys(map)) {
    const ks = String(k).trim();
    if (ks === String(userTypeId).trim()) {
      const p = tryKey(k);
      if (p !== null) return p;
      continue;
    }
    const n = Number(k);
    if (!Number.isNaN(n) && n === uid) {
      const p = tryKey(k);
      if (p !== null) return p;
    }
  }

  return null;
}

/** Checked state for one feature from its own `default_checked_permissions` + selected user type id. */
function checkedPermissionsForFeatureAndUserType(
  feature: { default_checked_permissions?: unknown },
  userTypeId: number,
): CheckedPermissionFlags {
  const raw = feature?.default_checked_permissions;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return normalizePermissionRow(undefined);
  }
  const picked = pickCheckedPermissionsForUserType(
    raw as Record<string, unknown>,
    userTypeId,
  );
  return picked ?? normalizePermissionRow(undefined);
}

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    background: "#fdffff",
    borderRadius: "12px",
    border: "1px solid #99d4e1",
    boxShadow: "8px 2px 58.4px 0px inset rgba(191, 242, 255, 0.2)",
    padding: 0,
    maxWidth: "800px",
    width: "100%",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
});

const ModalContent = styled(DialogContent)({
  padding: "32px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const ModalHeader = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const ModalTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "24px",
  fontWeight: 600,
  color: "#065b70",
  lineHeight: "normal",
});

const ModalSubtitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 400,
  color: "#065b70",
  lineHeight: "normal",
});

const FormSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
});

const SectionTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "16px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
});

const FormRow = styled(Box)({
  display: "flex",
  gap: "16px",
  width: "100%",
});

const FormField = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
});

const FieldLabel = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: "14px",
    "& input": {
      padding: "12px 16px",
      fontFamily: '"Rethink Sans", sans-serif',
      fontSize: "14px",
      color: "#262626",
      "&::placeholder": {
        color: "#a3a3a3",
        opacity: 1,
      },
    },
    "&:hover": {
      border: "1px solid #bfe5ec",
    },
    "&.Mui-focused": {
      border: "1px solid #0a829d",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const StyledSelect = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: "14px",
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontFamily: '"Rethink Sans", sans-serif',
      fontSize: "14px",
      color: "#262626",
      "&.MuiSelect-select[aria-expanded='false']": {
        color: "#a3a3a3",
      },
    },
    "&:hover": {
      border: "1px solid #bfe5ec",
    },
    "&.Mui-focused": {
      border: "1px solid #0a829d",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const InlineActionRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  width: "100%",
});

const SecondaryLinkButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#065b70",
  border: "1px solid #bfe5ec",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "13px",
  fontWeight: 600,
  padding: "8px 14px",
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#e6fafc",
    border: "1px solid #5bbfd6",
  },
});
const CheckboxSection = styled(Box)({
  padding: "16px 0",
});

const StyledCheckbox = styled(FormControlLabel)({
  margin: 0,
  "& .MuiCheckbox-root": {
    color: "#0a829d",
    padding: "4px 8px 4px 0",
    "&.Mui-checked": {
      color: "#0a829d",
    },
  },
  "& .MuiFormControlLabel-label": {
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: "14px",
    fontWeight: 500,
    color: "#065b70",
    lineHeight: "normal",
  },
});


const ExistingUserNotice = styled(Box)({
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  borderRadius: "8px",
  border: "1px solid #3aa9c1",
  backgroundColor: "#eaf9fc",
  padding: "12px 14px",
});

const ExistingUserWarning = styled(Box)({
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  borderRadius: "8px",
  border: "1px solid #f2c94c",
  backgroundColor: "#fff8db",
  padding: "12px 14px",
});

const ButtonRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  justifyContent: "center",
  marginTop: "8px",
}));

const CancelButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#737373",
  border: "1px solid #e0e0e0",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    border: "1px solid #bfe5ec",
  },
});

const SendButton = styled(Button)({
  background: "#0a829d",
  color: "white",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&:hover": {
    background: "#085f73",
  },
  "&:disabled": {
    background: "#b0c4c8",
    color: "#e6fafc",
  },
});

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  facilityId?: number;
  onUserAdded?: (info?: {
    userId?: number;
    facilityId?: number;
    userTypeId?: number;
  }) => void;
  availableFacilities?: Array<{
    id: number;
    name: string;
    org_name?: string;
    city?: string;
    state?: string;
    address_1?: string | null;
    address_2?: string | null;
    full_address?: string | null;
    npi_number?: string | null;
    ccn_number?: string | null;
    is_affable_connected?: boolean;
  }>;
}

interface Facility {
  id: number;
  name: string;
  org_name?: string;
  is_affable_connected?: boolean;
  city?: string;
  state?: string;
  address_1?: string | null;
  address_2?: string | null;
  full_address?: string | null;
  npi_number?: string | null;
  ccn_number?: string | null;
  country_code?: string | null;
}

const PERMISSIONS_SESSION_STORAGE_KEY = "user_management_pending_permissions";

const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  onClose,
  facilityId,
  onUserAdded,
  availableFacilities = [],
}) => {
  const [selectedFacility, setSelectedFacility] = useState<number | "">(
    facilityId || "",
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [surveySubtypes, setSurveySubtypes] = useState<string[]>([]);
  const [bypassConfirmation, setBypassConfirmation] = useState(false);
  const [createInAffableBPM, setCreateInAffableBPM] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [loadingUserTypes, setLoadingUserTypes] = useState(false);

  // Validation error states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showDefaultsOpen, setShowDefaultsOpen] = useState(false);
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [defaultsError, setDefaultsError] = useState<string | null>(null);
  const [defaultFeatures, setDefaultFeatures] = useState<any[]>([]);
  const [checkingExistingUser, setCheckingExistingUser] = useState(false);
  const [existingUser, setExistingUser] = useState<ExistingUserData | null>(null);
  const [existingUserAssignedToFacility, setExistingUserAssignedToFacility] =
    useState(false);
  const [prefilledFromExistingUser, setPrefilledFromExistingUser] =
    useState(false);
  // Permission matrix to track edited permissions: { featureId: { view, add, update, delete } }
  const [permissionMatrix, setPermissionMatrix] = useState<
    Record<
      string | number,
      { view: boolean; add: boolean; update: boolean; delete: boolean }
    >
  >({});

  // Use facilities from props (from API), no fallback to hardcoded facilities
  const facilities: Facility[] = useMemo(() => availableFacilities || [], [availableFacilities]);

  // Helper function to save permissions to sessionStorage
  const savePermissionsToSession = (
    matrix: Record<
      string | number,
      { view: boolean; add: boolean; update: boolean; delete: boolean }
    >,
  ) => {
    if (typeof window === "undefined") return;
    try {
      const normalized: Record<string, CheckedPermissionFlags> = {};
      for (const key of Object.keys(matrix as object)) {
        normalized[key] = normalizePermissionRow(
          (matrix as Record<string, unknown>)[key],
        );
      }
      sessionStorage.setItem(
        PERMISSIONS_SESSION_STORAGE_KEY,
        JSON.stringify(normalized),
      );
    } catch (error) {
      console.error("Error saving permissions to sessionStorage:", error);
    }
  };

  // Helper function to load permissions from sessionStorage
  const loadPermissionsFromSession = (): Record<
    string | number,
    CheckedPermissionFlags
  > => {
    if (typeof window === "undefined") return {};
    try {
      const stored = sessionStorage.getItem(PERMISSIONS_SESSION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, unknown>;
        const out: Record<string, CheckedPermissionFlags> = {};
        for (const key of Object.keys(parsed)) {
          out[key] = normalizePermissionRow(parsed[key]);
        }
        return out;
      }
    } catch (error) {
      console.error("Error loading permissions from sessionStorage:", error);
    }
    return {};
  };

  // Helper function to clear permissions from sessionStorage
  const clearPermissionsFromSession = () => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.removeItem(PERMISSIONS_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing permissions from sessionStorage:", error);
    }
  };

  // Determine whether the currently selected facility is connected to AffableBPM.
  // We expect each facility object (when provided) to optionally include this flag.
  const isAffableConnected = useMemo(() => {
    const current = facilities.find((f) => f.id === selectedFacility) ?? null;
    return Boolean(current?.is_affable_connected);
  }, [facilities, selectedFacility]);

  // Default the checkbox based on Affable connection status when modal opens
  React.useEffect(() => {
    if (open) {
      setCreateInAffableBPM(isAffableConnected);
    }
  }, [open, isAffableConnected]);

  // Update selected facility when facilityId prop changes
  React.useEffect(() => {
    if (facilityId) {
      setSelectedFacility(facilityId);
    } else {
      setSelectedFacility("");
    }
  }, [facilityId]);

  // Load saved permissions from sessionStorage when modal opens
  React.useEffect(() => {
    if (open && defaultFeatures.length > 0) {
      const savedPermissions = loadPermissionsFromSession();
      if (Object.keys(savedPermissions).length > 0) {
        // Merge saved permissions with current features
        const mergedMatrix: Record<
          string | number,
          { view: boolean; add: boolean; update: boolean; delete: boolean }
        > = {};
        defaultFeatures.forEach((f: any) => {
          mergedMatrix[f.id] = getPermissionRowFromMatrix(
            savedPermissions,
            f.id,
          );
        });
        setPermissionMatrix(mergedMatrix);
      }
    }
  }, [open, defaultFeatures]);

  // Fetch user types when modal opens
  React.useEffect(() => {
    if (open) {
      const fetchUserTypes = async () => {
        try {
          setLoadingUserTypes(true);
          const response = await getUserTypes({
            page: 1,
            page_size: 20,
            filters: { is_active: true },
          });
          if (response.success && response.data.items) {
            setUserTypes(response.data.items);
          }
        } catch (error: any) {
          console.error("Error fetching user types:", error);
          toast.error("Failed to load user types. Using default options.");
          // Fallback to default user types if API fails
          setUserTypes([]);
        } finally {
          setLoadingUserTypes(false);
        }
      };
      fetchUserTypes();
    }
  }, [open]);

  // Get the selected user type object to access subtypes
  const selectedUserTypeObject = userTypes.find(
    (type) => type.name === userType,
  );

  // Get available subtypes for the selected user type
  const availableSubtypes =
    selectedUserTypeObject?.user_subtypes?.filter(
      (subtype) => subtype.is_active && !subtype.is_deleted,
    ) || [];

  // Reset subtypes when user type changes
  React.useEffect(() => {
    setSurveySubtypes([]);
  }, [userType]);

  // Loaded feature list and matrix are for the previous user type — clear so we do not
  // show another type's defaults or merge stale session into the wrong grid.
  // Also drop sessionStorage: Facility Admin checked "Add" must not stick onto Standard User.
  React.useEffect(() => {
    setDefaultFeatures([]);
    setPermissionMatrix({});
    setShowDefaultsOpen(false);
    setDefaultsError(null);
    clearPermissionsFromSession();
  }, [userType]);

  // Check whether entered email already belongs to an existing user.
  React.useEffect(() => {
    if (!open) return;

    const trimmedEmail = email.trim().toLowerCase();
    const emailValidationError = validateEmail(trimmedEmail);

    if (!trimmedEmail || emailValidationError) {
      setCheckingExistingUser(false);
      setExistingUser(null);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        setCheckingExistingUser(true);
        const response = await checkUserExistsByEmail(
          trimmedEmail,
          typeof selectedFacility === "number" ? selectedFacility : undefined,
        );
        if (response.success && response.exists && response.data) {
          setExistingUser(response.data);
          const assignedFlag = Boolean(response.assigned_to_facility);
          const associationFacilityId = response.association?.facility_id;
          const matchesSelectedFacility =
            typeof selectedFacility === "number" &&
            typeof associationFacilityId === "number"
              ? associationFacilityId === selectedFacility
              : true;
          setExistingUserAssignedToFacility(assignedFlag && matchesSelectedFacility);
        } else {
          setExistingUser(null);
          setExistingUserAssignedToFacility(false);
        }
      } catch (error) {
        setExistingUser(null);
        setExistingUserAssignedToFacility(false);
      } finally {
        setCheckingExistingUser(false);
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [email, open, selectedFacility]);

  // Auto-fill and lock name fields when the email belongs to an existing user.
  React.useEffect(() => {
    if (existingUser) {
      setFirstName(existingUser.first_name || "");
      setLastName(existingUser.last_name || "");
      setFirstNameError("");
      setLastNameError("");
      setPrefilledFromExistingUser(true);
      return;
    }

    // If fields were previously auto-filled, clear them when no existing user is found.
    if (prefilledFromExistingUser) {
      setFirstName("");
      setLastName("");
      setPrefilledFromExistingUser(false);
    }
  }, [existingUser, prefilledFromExistingUser]);

  const loadDefaultPermissions = async () => {
    if (!selectedUserTypeObject) {
      toast.error("Please select a user type first");
      return;
    }
    try {
      setDefaultsLoading(true);
      setDefaultsError(null);
      const featuresResp = await fetchFeatures({
        page: 1,
        pageSize: 200,
        filters: { user_type_id: selectedUserTypeObject.id },
      });
      const feats =
        (featuresResp as any)?.result?.data ||
        (featuresResp as any)?.results ||
        (featuresResp as any)?.items ||
        [];
      setDefaultFeatures(feats);

      const savedPermissions = loadPermissionsFromSession();
      const selectedUserTypeId = selectedUserTypeObject.id;

      const initialMatrix: Record<
        string | number,
        { view: boolean; add: boolean; update: boolean; delete: boolean }
      > = {};
      feats.forEach((f: any) => {
        const saved =
          savedPermissions[f.id] ??
          savedPermissions[String(f.id)] ??
          savedPermissions[Number(f.id)];
        if (saved !== undefined && saved !== null) {
          initialMatrix[f.id] = normalizePermissionRow(saved);
          return;
        }

        const perTypeMap = f?.default_checked_permissions;
        const featureHasDefaultChecked =
          perTypeMap &&
          typeof perTypeMap === "object" &&
          !Array.isArray(perTypeMap) &&
          Object.keys(perTypeMap).length > 0;

        // API sends default_checked_permissions on each feature, keyed by user type id.
        if (featureHasDefaultChecked) {
          initialMatrix[f.id] = checkedPermissionsForFeatureAndUserType(
            f,
            selectedUserTypeId,
          );
          return;
        }

        initialMatrix[f.id] = normalizePermissionRow(undefined);
      });

      const hasSessionPermissions = Object.keys(savedPermissions).length > 0;

      // Existing user: overlay user-level (facility_id null) rows from GET permissions
      if (existingUser?.id && !hasSessionPermissions) {
        try {
          const permResp = await fetchUserPermissionsByUserId(existingUser.id);
          const permRows =
            (permResp as any)?.result?.data ??
            permResp?.permissions ??
            (permResp as any)?.items ??
            (permResp as any)?.results ??
            [];
          if (Array.isArray(permRows) && permRows.length > 0) {
            const byFeatureId = new Map<number, Record<string, unknown>>();
            for (const r of permRows) {
              if (!r || typeof r !== "object") continue;
              const row = r as Record<string, unknown>;
              const fid = Number(row.feature_id);
              if (Number.isNaN(fid)) continue;
              const prev = byFeatureId.get(fid);
              const rowGlobal =
                row.facility_id == null ||
                row.facility_id === "" ||
                row.facility_id === undefined;
              const prevGlobal =
                prev &&
                (prev.facility_id == null ||
                  prev.facility_id === "" ||
                  prev.facility_id === undefined);
              if (!prev || (rowGlobal && !prevGlobal)) {
                byFeatureId.set(fid, row);
              }
            }
            feats.forEach((f: any) => {
              if (f.feature_level !== 2) return;
              const row = byFeatureId.get(Number(f.id));
              if (row) {
                initialMatrix[f.id] = normalizePermissionRow({
                  view: row.view,
                  add: row.add,
                  update: row.update,
                  delete: row.delete,
                });
              }
            });
          }
        } catch (permErr) {
          console.warn(
            "[AddUserModal] Could not load existing user permissions; using defaults for user-level features.",
            permErr,
          );
        }
      }

      setPermissionMatrix(initialMatrix);
      savePermissionsToSession(initialMatrix);

      setShowDefaultsOpen(true);
    } catch (error: any) {
      console.error("Failed to load default permissions", error);
      setDefaultsError(error?.message || "Failed to load default permissions.");
      setShowDefaultsOpen(true);
    } finally {
      setDefaultsLoading(false);
    }
  };

  // Helper functions for permissions management (matching UserManagement)
  const togglePermission = (
    featureId: string | number,
    field: "view" | "add" | "update" | "delete",
  ) => {
    setPermissionMatrix((prev) => {
      const current = getPermissionRowFromMatrix(prev, featureId);
      const newValue = !current[field];

      const featureMeta = defaultFeatures.find(
        (f: any) =>
          f.id === featureId ||
          String(f.id) === String(featureId) ||
          (!Number.isNaN(Number(f.id)) &&
            !Number.isNaN(Number(featureId)) &&
            Number(f.id) === Number(featureId)),
      );

      // If unchecking view, automatically uncheck all other permissions
      if (field === "view" && newValue === false) {
        const updated = {
          ...prev,
          [featureId]: {
            view: false,
            add: false,
            update: false,
            delete: false,
          },
        };
        // Save to sessionStorage
        savePermissionsToSession(updated);
        return updated;
      }

      // If checking add, update, or delete, and view is currently unchecked, auto-check view
      const shouldAutoCheckView =
        (field === "add" || field === "update" || field === "delete") &&
        newValue === true &&
        current.view === false &&
        Boolean(featureMeta?.default_permissions?.view);

      const updated = {
        ...prev,
        [featureId]: {
          ...current,
          [field]: newValue,
          ...(shouldAutoCheckView && { view: true }),
        },
      };
      // Save to sessionStorage
      savePermissionsToSession(updated);
      return updated;
    });
  };

  const toggleAllPermissions = (
    field: "view" | "add" | "update" | "delete",
    value: boolean,
    levelFeatures: any[],
  ) => {
    setPermissionMatrix((prev) => {
      const next = { ...prev };
      levelFeatures.forEach((f) => {
        // Check if this checkbox is disabled based on default_permissions
        const isDisabled = (() => {
          if (field === "view") return !Boolean(f?.default_permissions?.view);
          if (field === "add") return !Boolean(f?.default_permissions?.add);
          if (field === "update")
            return !Boolean(f?.default_permissions?.update);
          if (field === "delete")
            return !Boolean(f?.default_permissions?.delete);
          return false;
        })();

        // Skip disabled checkboxes - don't change their state
        if (isDisabled) {
          return;
        }

        const current = getPermissionRowFromMatrix(prev, f.id);

        // If unchecking view, automatically uncheck all other permissions (only for enabled checkboxes)
        if (field === "view" && value === false) {
          next[f.id] = {
            view: false,
            add: false,
            update: false,
            delete: false,
          };
          return;
        }

        // If checking add, update, or delete, and view is currently unchecked, auto-check view
        // But only if view is not disabled
        const shouldAutoCheckView =
          (field === "add" || field === "update" || field === "delete") &&
          value === true &&
          current.view === false &&
          Boolean(f?.default_permissions?.view); // Only auto-check view if it's not disabled

        next[f.id] = {
          ...current,
          [field]: value,
          ...(shouldAutoCheckView && { view: true }),
        };
      });
      // Save to sessionStorage
      savePermissionsToSession(next);
      return next;
    });
  };

  // Calculate checked states for a specific level
  // Only considers enabled checkboxes (those with default_permissions set)
  const getLevelCheckedStates = (levelFeatures: any[]) => {
    // Filter to only enabled features for each permission type
    const enabledViewFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.view),
    );
    const enabledAddFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.add),
    );
    const enabledUpdateFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.update),
    );
    const enabledDeleteFeatures = levelFeatures.filter((f) =>
      Boolean(f?.default_permissions?.delete),
    );

    const allViewChecked =
      enabledViewFeatures.length > 0 &&
      enabledViewFeatures.every(
        (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).view,
      );
    const someViewChecked = enabledViewFeatures.some(
      (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).view,
    );

    const allAddChecked =
      enabledAddFeatures.length > 0 &&
      enabledAddFeatures.every(
        (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).add,
      );
    const someAddChecked = enabledAddFeatures.some(
      (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).add,
    );

    const allUpdateChecked =
      enabledUpdateFeatures.length > 0 &&
      enabledUpdateFeatures.every(
        (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).update,
      );
    const someUpdateChecked = enabledUpdateFeatures.some(
      (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).update,
    );

    const allDeleteChecked =
      enabledDeleteFeatures.length > 0 &&
      enabledDeleteFeatures.every(
        (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).delete,
      );
    const someDeleteChecked = enabledDeleteFeatures.some(
      (f) => getPermissionRowFromMatrix(permissionMatrix, f.id).delete,
    );

    return {
      allViewChecked,
      someViewChecked,
      allAddChecked,
      someAddChecked,
      allUpdateChecked,
      someUpdateChecked,
      allDeleteChecked,
      someDeleteChecked,
    };
  };

  const handleSubmit = async () => {
    // Validate all fields before submission
    const firstNameErr = validateFirstName(firstName);
    const lastNameErr = validateLastName(lastName);
    const emailErr = validateEmail(email);

    setFirstNameError(firstNameErr);
    setLastNameError(lastNameErr);
    setEmailError(emailErr);

    if (firstNameErr || lastNameErr || emailErr) {
      toast.error("Please fix the validation errors before submitting");
      return;
    }

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = email.trim();

    if (!userType || !selectedFacility) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      // Get the user type ID from the selected user type
      if (!selectedUserTypeObject) {
        toast.error("Please select a valid user type");
        setLoading(false);
        return;
      }

      // Convert selected subtype name to ID (get first selected subtype)
      let selectedSubtypeId: number | undefined;
      if (surveySubtypes.length > 0 && availableSubtypes.length > 0) {
        const firstSubtypeName = surveySubtypes[0];
        const subtype = availableSubtypes.find(
          (s) => s.name === firstSubtypeName,
        );
        if (subtype) {
          selectedSubtypeId = subtype.id;
        }
      }

      const matrixForSubmit =
        Object.keys(permissionMatrix).length > 0
          ? permissionMatrix
          : loadPermissionsFromSession();

      let permissionsPayload: Array<
        SavePermissionInput & { facility_id: number }
      > = [];
      if (defaultFeatures.length > 0) {
        permissionsPayload = defaultFeatures.map((f: any) => {
          const edited = getPermissionRowFromMatrix(matrixForSubmit, f.id);
          return {
            feature_id: f.id,
            facility_id: selectedFacility,
            view: edited.view,
            add: edited.add,
            update: edited.update,
            delete: edited.delete,
          };
        });
      } else if (Object.keys(matrixForSubmit).length > 0) {
        permissionsPayload = Object.keys(matrixForSubmit).map((id) => {
          const featureId = Number(id);
          const edited = getPermissionRowFromMatrix(
            matrixForSubmit,
            Number.isNaN(featureId) ? id : featureId,
          );
          return {
            feature_id: Number.isNaN(featureId) ? id : featureId,
            facility_id: selectedFacility,
            view: edited.view,
            add: edited.add,
            update: edited.update,
            delete: edited.delete,
          };
        });
      }

      if (permissionsPayload.length === 0) {
        const featuresResp = await fetchFeatures({
          page: 1,
          pageSize: 200,
          filters: { user_type_id: selectedUserTypeObject.id },
        });
        const feats =
          (featuresResp as any)?.result?.data ||
          (featuresResp as any)?.results ||
          (featuresResp as any)?.items ||
          [];
        permissionsPayload = feats.map((f: any) => {
          const hasMatrixRow =
            Object.prototype.hasOwnProperty.call(matrixForSubmit, f.id) ||
            Object.prototype.hasOwnProperty.call(
              matrixForSubmit,
              String(f.id),
            );
          const flags = hasMatrixRow
            ? getPermissionRowFromMatrix(matrixForSubmit, f.id)
            : checkedPermissionsForFeatureAndUserType(
                f,
                selectedUserTypeObject.id,
              );
          return {
            feature_id: f.id,
            facility_id: selectedFacility,
            view: flags.view,
            add: flags.add,
            update: flags.update,
            delete: flags.delete,
          };
        });
      }

      const selectedFacilityObj = facilities.find(
        (f) => f.id === selectedFacility,
      );
      console.log("facilities:", facilities);
      console.log("selectedFacilityObj:", selectedFacilityObj);

      const payload: SendEmailAddUserPayload = {
        facility_id: selectedFacility,
        first_name: trimmedFirst,
        last_name: trimmedLast,
        email: trimmedEmail,
        user_type_id: selectedUserTypeObject.id,
        ...(selectedSubtypeId !== undefined && {
          user_subtype_id: selectedSubtypeId,
        }),
        bypass_confirmation: existingUser ? true : bypassConfirmation,
        is_affable_bpm_user: createInAffableBPM,
        ...(permissionsPayload.length > 0
          ? { permissions: permissionsPayload }
          : {}),
        org_name: selectedFacilityObj?.org_name,
      };
      console.log("payload:", payload);
      const response = await sendEmailAddUser(payload);

      if (response.success) {
        // If permissions were not included in the API response, save them separately as fallback
        if (
          permissionsPayload &&
          permissionsPayload.length > 0 &&
          (response.user_id || existingUser?.id)
        ) {
          try {
            await saveUserPermissions({
              user_id: response.user_id ?? existingUser!.id,
              facility_id: selectedFacility,
              features: permissionsPayload,
            });
          } catch (permError) {
            console.warn("Failed to save permissions for new user:", permError);
            // Don't show error if permissions were already saved via the API
            if (!payload.permissions) {
              toast.warn("User created, but failed to save permissions.");
            }
          }
        }

        const affableSync = response.affable_bpm_sync;
        if (affableSync) {
          const payloadDetails = (
            affableSync.details?.payload &&
            typeof affableSync.details.payload === "object"
              ? affableSync.details.payload
              : null
          ) as Record<string, unknown> | null;
          const payloadMessage =
            payloadDetails && "ErrorMsg" in payloadDetails
              ? String(
                  (payloadDetails as Record<string, unknown>).ErrorMsg ?? "",
                )
              : null;
          const syncMessage =
            payloadMessage ||
            affableSync.details?.message ||
            affableSync.error ||
            "";
          const finalMessage =
            syncMessage?.trim() ||
            (affableSync.success
              ? "User synced with AffableFLOW successfully."
              : "Failed to sync user with AffableFLOW.");
          if (affableSync.success) {
            toast.success(`User added successfully in AffableFLOW`);
          } else {
            toast.error(`AffableFLOW: ${finalMessage}`);
          }
        }
        // Show success message based on user flow
        if (existingUser) {
          toast.success(
            `${existingUser.display_name || `${existingUser.first_name} ${existingUser.last_name}`.trim() || email} has been added to the facility successfully!`,
          );
        } else if (bypassConfirmation) {
          toast.success(
            `User ${firstName} ${lastName} has been created successfully!`,
          );
        } else {
          toast.success(`Invitation email sent to ${email} successfully!`);
        }

        // Clear permissions from sessionStorage after successful invitation
        clearPermissionsFromSession();

        // Reset form
        resetForm();

        // Close modal and refresh user list
        onUserAdded?.({
          userId: response.user_id ?? undefined,
          facilityId: selectedFacility,
          userTypeId: selectedUserTypeObject.id,
        });
        onClose();
      } else {
        // Handle API error response
        const errorMessage =
          response.error?.message || response.message || "Failed to add user";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error adding user:", error);

      // Handle different types of errors
      if (error.message) {
        toast.error(error.message);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          "An error occurred while adding the user. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    resetForm();
    onClose();
  };

  const handleClosePermissionsDialog = () => {
    setShowDefaultsOpen(false);
  };

  const handleSavePermissionsDialog = () => {
    savePermissionsToSession(permissionMatrix);
    handleClosePermissionsDialog();
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setUserType("");
    setSurveySubtypes([]);
    setBypassConfirmation(false);
    setCreateInAffableBPM(false);
    setPermissionMatrix({});
    setDefaultFeatures([]);
    setShowDefaultsOpen(false);
    setCheckingExistingUser(false);
    setExistingUser(null);
    setExistingUserAssignedToFacility(false);
    setPrefilledFromExistingUser(false);
    // Reset validation errors
    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    // Note: We don't clear sessionStorage here because permissions should persist
    // until the invitation is successfully sent (handled in handleSubmit)
  };

  // Validation helpers
  const validateFirstName = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "First Name is required";
    }
    return "";
  };

  const validateLastName = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Last Name is required";
    }
    return "";
  };

  const validateEmail = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const isFormValid = Boolean(
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    !firstNameError &&
    !lastNameError &&
    !emailError &&
    userType &&
    selectedFacility &&
    facilities.length > 0,
    // Subtype validation commented out with dropdown
    // && (availableSubtypes.length === 0 || surveySubtypes.length > 0)
  );

  const isAlreadyAddedToFacility = Boolean(
    existingUser && existingUserAssignedToFacility,
  );

  return (
    <StyledDialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add Users to Facility</ModalTitle>
          <ModalSubtitle>
            Add new users to your facility and manage their access
          </ModalSubtitle>
        </ModalHeader>

        <FormSection>
          <SectionHeader>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 6C3 4.34315 4.34315 3 6 3H14C15.6569 3 17 4.34315 17 6V14C17 15.6569 15.6569 17 14 17H6C4.34315 17 3 15.6569 3 14V6Z"
                stroke="#065b70"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M7 8H13M7 12H11"
                stroke="#065b70"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <SectionTitle>Select Facility</SectionTitle>
          </SectionHeader>

          {facilities.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "14px",
                  color: "#737373",
                  fontStyle: "italic",
                }}
              >
                No facilities available. Please add a facility first.
              </Typography>
            </Box>
          ) : (
            <StyledSelect fullWidth>
              <Select
                value={selectedFacility}
                onChange={(e) =>
                  setSelectedFacility(e.target.value as number | "")
                }
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: "#a3a3a3" }}>
                        Select a Facility
                      </span>
                    );
                  }
                  const facility = facilities.find((f) => f.id === selected);
                  return (
                    <span style={{ color: "#262626" }}>
                      {facility
                        ? formatFacilityDropdownLabel({
                            facility_name:
                              facility.name || `Facility ${facility.id}`,
                            city: facility.city,
                            state: facility.state,
                            address_1: facility.address_1,
                            address_2: facility.address_2,
                            full_address: facility.full_address,
                            country_code: facility.country_code,
                            ccn_number:
                              facility.npi_number ?? facility.ccn_number,
                          })
                        : `Facility ${selected}`}
                    </span>
                  );
                }}
              >
                <MenuItem value="" disabled>
                  Select a Facility
                </MenuItem>
                {facilities.map((facility) => (
                  <MenuItem key={facility.id} value={facility.id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <span>
                        {formatFacilityDropdownLabel({
                          facility_name:
                            facility.name || `Facility ${facility.id}`,
                          city: facility.city,
                          state: facility.state,
                          address_1: facility.address_1,
                          address_2: facility.address_2,
                          full_address: facility.full_address,
                          country_code: facility.country_code,
                          ccn_number:
                            facility.npi_number ?? facility.ccn_number,
                        })}
                      </span>
                      {facility.is_affable_connected && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AffableConnectedIcon />
                        </Box>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </StyledSelect>
          )}
        </FormSection>

        <FormSection>
         
          <FormRow>
            <FormField>
              <FieldLabel>Email</FieldLabel>
              <StyledTextField
                placeholder="Enter Email Address"
                type="email"
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  if (emailError) {
                    setEmailError(validateEmail(value));
                  }
                }}
                onBlur={(e) => {
                  setEmailError(validateEmail(e.target.value));
                }}
                error={!!emailError}
                helperText={emailError}
                required
                fullWidth
              />
            </FormField>

            <FormField>
              <FieldLabel>User Type</FieldLabel>
              <StyledSelect fullWidth>
                <Select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                  displayEmpty
                  disabled={loadingUserTypes}
                >
                  <MenuItem value="" disabled>
                    {loadingUserTypes
                      ? "Loading user types..."
                      : userTypes.length === 0
                        ? "No user types available"
                        : "Select User Type"}
                  </MenuItem>
                  {userTypes.map((type) => (
                    <MenuItem key={type.id} value={type.name}>
                      {formatUserTypeDisplayName(type.name)}
                    </MenuItem>
                  ))}
                </Select>
              </StyledSelect>
            </FormField>
          </FormRow>

          {checkingExistingUser && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#0a829d" }} />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "13px",
                  color: "#065b70",
                }}
              >
                Checking user...
              </Typography>
            </Box>
          )}

          {existingUser &&
            (isAlreadyAddedToFacility ? (
              <ExistingUserWarning>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    mt: "2px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    border: "2px solid #b7791f",
                    color: "#b7791f",
                    fontSize: "10px",
                    fontWeight: 700,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  !
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#7a4f10",
                      lineHeight: "20px",
                    }}
                  >
                    This user is already added to this facility.
                  </Typography>
                </Box>
              </ExistingUserWarning>
            ) : (
              <ExistingUserNotice>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    mt: "2px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    border: "2px solid #0a829d",
                    color: "#0a829d",
                    fontSize: "10px",
                    fontWeight: 700,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  i
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      lineHeight: "20px",
                    }}
                  >
                    This person already has a Unicomply account.
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#516b72",
                      lineHeight: "20px",
                    }}
                  >
                    They will be added directly to the selected facility - no
                    sign-up needed. A notification email will be sent to let them
                    know.
                  </Typography>
                </Box>
              </ExistingUserNotice>
            ))}
          <FormRow>
            <FormField>
              <FieldLabel>First Name</FieldLabel>
              <StyledTextField
                placeholder="Enter First Name"
                value={firstName}
                disabled={Boolean(existingUser)}
                onChange={(e) => {
                  const value = e.target.value;
                  setFirstName(value);
                  if (firstNameError) {
                    setFirstNameError(validateFirstName(value));
                  }
                }}
                onBlur={(e) => {
                  setFirstNameError(validateFirstName(e.target.value));
                }}
                error={!!firstNameError}
                helperText={firstNameError}
                required
                fullWidth
              />
            </FormField>

            <FormField>
              <FieldLabel>Last Name</FieldLabel>
              <StyledTextField
                placeholder="Enter Last Name"
                value={lastName}
                disabled={Boolean(existingUser)}
                onChange={(e) => {
                  const value = e.target.value;
                  setLastName(value);
                  if (lastNameError) {
                    setLastNameError(validateLastName(value));
                  }
                }}
                onBlur={(e) => {
                  setLastNameError(validateLastName(e.target.value));
                }}
                error={!!lastNameError}
                helperText={lastNameError}
                required
                fullWidth
              />
            </FormField>
          </FormRow>
          {/* User Sub Type dropdown - commented out */}
          {/* {availableSubtypes.length > 0 && (
            <FormRow>
              <FormField sx={{ width: "50%", maxWidth: "50%" }}>
                <FieldLabel>User Sub Type</FieldLabel>
                <StyledSelect fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={surveySubtypes}
                    renderValue={(selected) =>
                      selected.length === 0 ? (
                        <span style={{ color: "#a3a3a3" }}>
                          Select Sub Type(s)
                        </span>
                      ) : (
                        (selected as string[]).join(", ")
                      )
                    }
                    onChange={(e) =>
                      setSurveySubtypes(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : (e.target.value as string[])
                      )
                    }
                  >
                    {availableSubtypes.map((subtype) => (
                      <MenuItem key={subtype.id} value={subtype.name}>
                        <Checkbox
                          checked={surveySubtypes.indexOf(subtype.name) > -1}
                        />
                        {subtype.name}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledSelect>
              </FormField>
            </FormRow>
          )} */}
        </FormSection>

        <InlineActionRow>
          <Box />
          <SecondaryLinkButton onClick={loadDefaultPermissions}>
            Add permissions
          </SecondaryLinkButton>
        </InlineActionRow>

        {isAffableConnected && userType !== "External User" && (
          <CheckboxSection>
            <StyledCheckbox
              control={
                <Checkbox
                  checked={createInAffableBPM}
                  onChange={(e) => setCreateInAffableBPM(e.target.checked)}
                />
              }
              label="Create user in AffableFLOW"
            />
          </CheckboxSection>
        )}

        {/* <CheckboxSection>
          <StyledCheckbox
            control={
              <Checkbox
                checked={bypassConfirmation}
                onChange={(e) => setBypassConfirmation(e.target.checked)}
              />
            }
            label="Create user immediately (bypass email confirmation)"
          />
          <CheckboxDescription>
            If checked, the user account will be created immediately with a temporary password. If unchecked, an invitation email will be sent for the user to complete registration.
          </CheckboxDescription>
        </CheckboxSection> */}

        <ButtonRow>
          <CancelButton onClick={handleCancel} disabled={loading}>
            Cancel
          </CancelButton>
          <SendButton
            onClick={handleSubmit}
            disabled={!isFormValid || loading || isAlreadyAddedToFacility}
          >
            {!loading && (
              existingUser ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 4.16666V15.8333M4.16669 10H15.8334"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.5 2.5L8.75 11.25M17.5 2.5L11.875 17.5L8.75 11.25M17.5 2.5L2.5 8.125L8.75 11.25"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            )}
            {loading
              ? existingUser
                ? "Adding..."
                : "Sending..."
              : existingUser
                ? "Add to Facility"
                : "Send Invitation"}
          </SendButton>
        </ButtonRow>

        <Dialog
          open={showDefaultsOpen}
          onClose={(event, reason) => {
            // Prevent closing on backdrop click or escape key
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            handleClosePermissionsDialog();
          }}
          fullWidth
          maxWidth="md"
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          <DialogTitle
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#fff",
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: "18px",
                fontWeight: 600,
                color: "#065b70",
              }}
            >
              Edit Permissions
            </Typography>
            <IconButton
              onClick={handleClosePermissionsDialog}
              sx={{
                padding: "4px",
                color: "#737373",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  color: "#065b70",
                },
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              maxHeight: "60vh",
              overflow: "auto",
              position: "relative",
              pt: 3,
              px: 3,
              pb: 2,
            }}
          >
            {defaultsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {defaultsError}
              </Alert>
            )}
            {defaultsLoading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <CircularProgress size={20} sx={{ color: "#065b70" }} />
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: 14,
                    color: "#065b70",
                  }}
                >
                  Loading permissions...
                </Typography>
              </Box>
            )}
            {!defaultsLoading &&
              (() => {
                // Group features by feature_level
                const permissionEditorFeatures =
                  filterFeaturesForPermissionEditor(defaultFeatures);
                const facilityFeatures = permissionEditorFeatures.filter(
                  (f) => f.feature_level === 1,
                );
                const userFeatures = permissionEditorFeatures.filter(
                  (f) => f.feature_level === 2,
                );

                return (
                  <Box sx={{ overflowX: "auto" }}>
                    {/* User Level Features */}
                    {userFeatures.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          sx={{
                            fontFamily: '"Rethink Sans", sans-serif',
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#0a829d",
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                            mb: 2,
                          }}
                        >
                          User Level Features (All Facilities)
                        </Typography>
                        <MUITable
                          size="small"
                          sx={{ tableLayout: "fixed", width: "100%" }}
                        >
                          <colgroup>
                            <col style={{ width: "40%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                          </colgroup>
                          <MUITableHead>
                            <MUITableRow>
                              <MUITableCell
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                Feature
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    View
                                  </Typography>
                                  {(() => {
                                    const userLevelStates =
                                      getLevelCheckedStates(userFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={userLevelStates.allViewChecked}
                                        indeterminate={
                                          !userLevelStates.allViewChecked &&
                                          userLevelStates.someViewChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "view",
                                            e.target.checked,
                                            userFeatures,
                                          )
                                        }
                                        disabled={
                                          userFeatures.length > 0 &&
                                          userFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.view,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    Add
                                  </Typography>
                                  {(() => {
                                    const userLevelStates =
                                      getLevelCheckedStates(userFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={userLevelStates.allAddChecked}
                                        indeterminate={
                                          !userLevelStates.allAddChecked &&
                                          userLevelStates.someAddChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "add",
                                            e.target.checked,
                                            userFeatures,
                                          )
                                        }
                                        disabled={
                                          userFeatures.length > 0 &&
                                          userFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.add,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    Edit
                                  </Typography>
                                  {(() => {
                                    const userLevelStates =
                                      getLevelCheckedStates(userFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          userLevelStates.allUpdateChecked
                                        }
                                        indeterminate={
                                          !userLevelStates.allUpdateChecked &&
                                          userLevelStates.someUpdateChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "update",
                                            e.target.checked,
                                            userFeatures,
                                          )
                                        }
                                        disabled={
                                          userFeatures.length > 0 &&
                                          userFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.update,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    Delete
                                  </Typography>
                                  {(() => {
                                    const userLevelStates =
                                      getLevelCheckedStates(userFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          userLevelStates.allDeleteChecked
                                        }
                                        indeterminate={
                                          !userLevelStates.allDeleteChecked &&
                                          userLevelStates.someDeleteChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "delete",
                                            e.target.checked,
                                            userFeatures,
                                          )
                                        }
                                        disabled={
                                          userFeatures.length > 0 &&
                                          userFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.delete,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                            </MUITableRow>
                          </MUITableHead>
                          <MUITableBody>
                            {userFeatures.map((feature) => {
                              const state = getPermissionRowFromMatrix(
                                permissionMatrix,
                                feature.id,
                              );
                              return (
                                <MUITableRow key={feature.id}>
                                  <MUITableCell
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: "13px",
                                      color: "#262626",
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    {getFeatureDisplayName(
                                      feature.feature_name ||
                                        feature.name ||
                                        String(
                                          feature.code ?? feature.id ?? "",
                                        ),
                                    )}
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        Boolean(
                                          feature?.default_permissions?.view,
                                        ) && state.view
                                      }
                                      onChange={() =>
                                        togglePermission(feature.id, "view")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.view,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        Boolean(
                                          feature?.default_permissions?.add,
                                        ) && state.add
                                      }
                                      onChange={() =>
                                        togglePermission(feature.id, "add")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.add,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        Boolean(
                                          feature?.default_permissions?.update,
                                        ) && state.update
                                      }
                                      onChange={() =>
                                        togglePermission(feature.id, "update")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.update,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        Boolean(
                                          feature?.default_permissions?.delete,
                                        ) && state.delete
                                      }
                                      onChange={() =>
                                        togglePermission(feature.id, "delete")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.delete,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                </MUITableRow>
                              );
                            })}
                          </MUITableBody>
                        </MUITable>
                      </Box>
                    )}

                    {/* Facility Level Features */}
                    {facilityFeatures.length > 0 && (
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: '"Rethink Sans", sans-serif',
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#0a829d",
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                            mb: 2,
                          }}
                        >
                          Facility Level Features
                        </Typography>
                        <MUITable
                          size="small"
                          sx={{ tableLayout: "fixed", width: "100%" }}
                        >
                          <colgroup>
                            <col style={{ width: "40%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "15%" }} />
                          </colgroup>
                          <MUITableHead>
                            <MUITableRow>
                              <MUITableCell
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                Feature
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    View
                                  </Typography>
                                  {(() => {
                                    const facilityLevelStates =
                                      getLevelCheckedStates(facilityFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          facilityLevelStates.allViewChecked
                                        }
                                        indeterminate={
                                          !facilityLevelStates.allViewChecked &&
                                          facilityLevelStates.someViewChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "view",
                                            e.target.checked,
                                            facilityFeatures,
                                          )
                                        }
                                        disabled={
                                          facilityFeatures.length > 0 &&
                                          facilityFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.view,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    Add
                                  </Typography>
                                  {(() => {
                                    const facilityLevelStates =
                                      getLevelCheckedStates(facilityFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          facilityLevelStates.allAddChecked
                                        }
                                        indeterminate={
                                          !facilityLevelStates.allAddChecked &&
                                          facilityLevelStates.someAddChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "add",
                                            e.target.checked,
                                            facilityFeatures,
                                          )
                                        }
                                        disabled={
                                          facilityFeatures.length > 0 &&
                                          facilityFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.add,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    Edit
                                  </Typography>
                                  {(() => {
                                    const facilityLevelStates =
                                      getLevelCheckedStates(facilityFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          facilityLevelStates.allUpdateChecked
                                        }
                                        indeterminate={
                                          !facilityLevelStates.allUpdateChecked &&
                                          facilityLevelStates.someUpdateChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "update",
                                            e.target.checked,
                                            facilityFeatures,
                                          )
                                        }
                                        disabled={
                                          facilityFeatures.length > 0 &&
                                          facilityFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.update,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                              <MUITableCell
                                align="center"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "12px",
                                  color: "#262626",
                                  padding: "8px 16px",
                                  borderBottom: "1px solid #e0e0e0",
                                  backgroundColor: "#e6fafc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    Delete
                                  </Typography>
                                  {(() => {
                                    const facilityLevelStates =
                                      getLevelCheckedStates(facilityFeatures);
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          facilityLevelStates.allDeleteChecked
                                        }
                                        indeterminate={
                                          !facilityLevelStates.allDeleteChecked &&
                                          facilityLevelStates.someDeleteChecked
                                        }
                                        onChange={(e) =>
                                          toggleAllPermissions(
                                            "delete",
                                            e.target.checked,
                                            facilityFeatures,
                                          )
                                        }
                                        disabled={
                                          facilityFeatures.length > 0 &&
                                          facilityFeatures.every(
                                            (f) =>
                                              !Boolean(
                                                f?.default_permissions?.delete,
                                              ),
                                          )
                                        }
                                        sx={{
                                          color: "#0a829d",
                                          padding: "4px",
                                          margin: 0,
                                          "&.Mui-checked": {
                                            color: "#0a829d",
                                          },
                                          "&.Mui-disabled": {
                                            color: "#bdbdbd",
                                            "& .MuiSvgIcon-root": {
                                              backgroundColor: "#e0e0e0",
                                              borderRadius: "4px",
                                            },
                                          },
                                        }}
                                      />
                                    );
                                  })()}
                                </Box>
                              </MUITableCell>
                            </MUITableRow>
                          </MUITableHead>
                          <MUITableBody>
                            {facilityFeatures.map((feature) => {
                              const state = getPermissionRowFromMatrix(
                                permissionMatrix,
                                feature.id,
                              );
                              return (
                                <MUITableRow key={feature.id}>
                                  <MUITableCell
                                    sx={{
                                      fontFamily: '"Rethink Sans", sans-serif',
                                      fontSize: "13px",
                                      color: "#262626",
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    {getFeatureDisplayName(
                                      feature.feature_name ||
                                        feature.name ||
                                        String(
                                          feature.code ?? feature.id ?? "",
                                        ),
                                    )}
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={state.view}
                                      onChange={() =>
                                        togglePermission(feature.id, "view")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.view,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={state.add}
                                      onChange={() =>
                                        togglePermission(feature.id, "add")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.add,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={state.update}
                                      onChange={() =>
                                        togglePermission(feature.id, "update")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.update,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                  <MUITableCell
                                    align="center"
                                    sx={{
                                      padding: "8px 16px",
                                      borderBottom: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={state.delete}
                                      onChange={() =>
                                        togglePermission(feature.id, "delete")
                                      }
                                      disabled={
                                        !Boolean(
                                          feature?.default_permissions?.delete,
                                        )
                                      }
                                      sx={{
                                        color: "#0a829d",
                                        padding: "4px",
                                        "&.Mui-checked": {
                                          color: "#0a829d",
                                        },
                                        "&.Mui-disabled": {
                                          color: "#bdbdbd",
                                          "& .MuiSvgIcon-root": {
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "4px",
                                          },
                                        },
                                      }}
                                    />
                                  </MUITableCell>
                                </MUITableRow>
                              );
                            })}
                          </MUITableBody>
                        </MUITable>
                      </Box>
                    )}
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions sx={{ padding: "16px 24px", gap: "12px" }}>
            <Button
              onClick={handleClosePermissionsDialog}
              sx={{
                backgroundColor: "transparent",
                color: "#737373",
                border: "1px solid #e0e0e0",
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 500,
                padding: "12px 24px",
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #bfe5ec",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePermissionsDialog}
              sx={{
                background: "#0a829d",
                color: "white",
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                padding: "12px 24px",
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": {
                  background: "#085f73",
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </ModalContent>
    </StyledDialog>
  );
};

export default AddUserModal;
